// The three catalogue-and-billing endpoints, as the portal sees them.
//
// The types here are the payload contract. They once had a rival: the design
// drop's `./fixtures.ts` carried `CmPack` / `CmInvoiceLine` with pre-formatted
// display strings ("$1.00", "29,204", "2 h ago"), because a prototype has no
// backend to be raw about. M-W38-02 deleted that file — a fixture shape beside
// a real one is not a placeholder once there is a real one. The endpoints send
// exact decimals as strings, currency codes and ISO timestamps (D6), and
// ../format.ts turns those into the words the prototype used to hardcode.
//
// Every amount arrives marked `estimate: true`. That mark is not advisory: this
// service is not the invoicing authority, and a figure rendered without saying
// so is a figure a customer will read as the amount owed. The pages label it,
// and the type makes the field impossible to ignore.
//
// Fetches go through `authorizedFetch` from the session provider, which bears
// the access token and renews it once if the service says it has expired. No
// screen reads the token itself.

import { useCallback, useEffect, useState } from 'react';

import { isWaitingOnActivation } from './presentation';

import { usePortalSession } from '../auth/context';

/** One metered thing a pack charges for, and what it costs. */
export interface PackDimension {
  key: string;
  unit: string;
  unit_short: string;
  /** Money per unit, exact, as a decimal string. */
  rate: string;
  /** ISO 4217 code the rate is quoted in. */
  currency: string;
}

/** A decision pack as sold: the catalogue half of an entry. */
export interface CataloguePack {
  name: string;
  pack_number: string;
  description: string;
  pricing: string;
  term: string;
  dimensions: PackDimension[];
}

/**
 * One agreement the customer holds, and the pack it is for.
 *
 * `pack` is null where no decision pack has been assigned yet — a subscription
 * that has arrived and is waiting on setup. That is not an error case to filter
 * out; it is the entry the customer most needs to act on, and the reason the
 * list is built from subscriptions rather than from the catalogue.
 *
 * `public_id` is the subscription's own identifier, and it is what makes an
 * entry actionable: the page where setup is finished is addressed by it. The
 * agreement ref names the agreement to a person and to the channel; it is not
 * an address here.
 */
export interface HeldPack {
  public_id: string;
  agreement_ref: string;
  channel: string;
  state: string;
  billable: boolean;
  started_at: string | null;
  ended_at: string | null;
  received_at: string;
  instance_count: number;
  running_instance_count: number;
  pack: CataloguePack | null;
}

export interface PacksPayload {
  packs: HeldPack[];
  instances: { total: number; running: number };
  dimensions_reporting: number;
}


/**
 * One period, dimension and agreement, priced.
 *
 * `priced` false — with `rate` and `amount` null — is a dimension the ledger
 * recorded and the catalogue does not price. The quantity is real and is shown;
 * the amount is left empty rather than guessed at.
 */
export interface BillingLine {
  period_start: string;
  agreement_ref: string;
  channel: string;
  pack_name: string | null;
  dimension: string;
  unit_short: string | null;
  quantity: string;
  hours: number;
  latest_submitted_at: string | null;
  priced: boolean;
  rate: string | null;
  amount: string | null;
  currency: string | null;
  estimate: true;
  basis: string;
}

/** A period total. `amount` null means the period could not be totalled. */
export interface PeriodTotal {
  period_start: string | null;
  amount: string | null;
  currency: string | null;
  mixed_currencies: boolean;
  estimate: true;
  basis: string;
  unpriced_lines: number;
}

export interface BillingPayload {
  lines: BillingLine[];
  current_period: PeriodTotal;
  previous_period: PeriodTotal;
  year_to_date: PeriodTotal;
  current_period_start: string;
  previous_period_start: string;
}

/**
 * One thing that happened, named by a token rather than a sentence.
 *
 * The backend composes this feed from records that already exist and writes no
 * prose — see catalog/activity.py. The sentence is this app's to write, which
 * is why ACTIVITY_LINE below lives here and not there.
 */
export interface ActivityEvent {
  kind: 'subscription' | 'metering' | 'fleet' | 'billing';
  event: string;
  tone: 'ok' | 'info' | 'warn';
  subject: string;
  occurred_at: string;
  channel?: string;
  state?: string;
  verb?: string;
  outcome?: string;
  usage_hour?: string;
}

export interface ActivityPayload {
  events: ActivityEvent[];
}

/**
 * The sentence for each event token.
 *
 * This is where a sales channel gets named, and it is the only place in the
 * stack that may: the core speaks metricsIQ vocabulary and translates a channel
 * at its adapter, so "AWS Marketplace" belongs in the app's copy rather than in
 * a payload. An unrecognised token renders as its subject alone rather than as a
 * blank line, so a backend that grows an event before this map does degrades
 * visibly instead of silently.
 */
/**
 * What a sales channel is called on screen, or null where it has no name.
 *
 * The app is the only layer allowed to name a channel — the core speaks
 * metricsIQ vocabulary and translates at the adapter — so this is the one
 * place a vendor's name is written down.
 *
 * NULL IS THE IMPORTANT RETURN. Not every channel is a sales channel: the
 * seeded door records subscriptions on a channel of its own, and
 * `register_instance` mints internal-channel subscriptions for a tenant that
 * precedes its sale. Neither is billable and neither came from a marketplace.
 * Callers must therefore be able to say nothing rather than assert something
 * false, which is what a default of "AWS Marketplace" did: on 2026-08-31 the
 * seeded customer's Home read "New subscription received from AWS Marketplace"
 * over a subscription that `adapter_for()` REFUSES to submit, and the Owner
 * reasonably concluded a Marketplace agreement existed.
 *
 * An unrecognised token also returns null rather than being rendered raw: a
 * channel this map has not learned yet is a channel whose customer-facing name
 * nobody has chosen, and printing its internal token would be a worse guess
 * than printing nothing.
 */
const CHANNEL_NAMES: Record<string, string> = {
  'aws-marketplace': 'AWS Marketplace',
  'azure-marketplace': 'Azure Marketplace',
};

export function channelName(channel?: string | null): string | null {
  if (!channel) return null;
  return CHANNEL_NAMES[channel] ?? null;
}

/**
 * Where a customer manages the subscription itself, per channel.
 *
 * Only a marketplace has one. The seeded and internal channels record
 * subscriptions this service minted, and there is no outside console to send
 * anybody to — so they are absent here rather than mapped to a plausible URL,
 * and every caller has to be able to draw nothing. That is the same rule
 * CHANNEL_NAMES above keeps, for the same reason: a destination nobody chose
 * is a worse answer than no link.
 */
const CHANNEL_CONSOLE_URLS: Record<string, string> = {
  'aws-marketplace': 'https://console.aws.amazon.com/marketplace/home#/subscriptions',
};

/** The console for this channel, or null where the channel has none. */
export function channelConsole(channel?: string | null): string | null {
  if (!channel) return null;
  return CHANNEL_CONSOLE_URLS[channel] ?? null;
}

/** Every distinct channel the customer's subscriptions arrived on. */
export function channelsHeld(packs: HeldPack[]): Set<string> {
  return new Set(packs.map((pack) => pack.channel));
}

/** A channel the customer holds, named, with somewhere to go. */
export interface ChannelConsole {
  name: string;
  url: string;
}

/**
 * The one marketplace this customer can be sent to, or null.
 *
 * Null covers three cases that all mean the same thing for a link: the
 * customer holds no marketplace subscription at all, the channel they hold has
 * no console this map knows, or they hold two marketplaces and no single
 * destination is the right one. A sidebar entry is one link; offering it under
 * any of the three would be naming a console by guess.
 */
export function soleChannelConsole(packs: HeldPack[]): ChannelConsole | null {
  const consoles: ChannelConsole[] = [];
  for (const channel of channelsHeld(packs)) {
    const url = CHANNEL_CONSOLE_URLS[channel];
    const name = CHANNEL_NAMES[channel];
    if (url && name) {
      consoles.push({ name, url });
    }
  }
  return consoles.length === 1 ? consoles[0] : null;
}

/**
 * The one channel these lines came from, or null where they disagree.
 *
 * A customer may hold subscriptions on more than one channel — that is what
 * the adapter boundary exists for — and the sentences built on this are single
 * sentences. Naming whichever channel happened to sort first would be a claim
 * about where a figure gets invoiced that is only true by luck, so a mixed set
 * names none and `invoicedBy` falls back to the channel-free wording.
 *
 * Here rather than on one page, so Home, Billing and the shell all derive the
 * channel the same way instead of each keeping its own copy of the rule.
 */
export function soleChannel(lines: { channel: string }[]): string | null {
  const channels = new Set(lines.map((line) => line.channel));
  return channels.size === 1 ? [...channels][0] : null;
}

/** " from AWS Marketplace", or "" where the channel has no name to give. */
export function fromChannel(channel?: string | null): string {
  const name = channelName(channel);
  return name ? ` from ${name}` : '';
}

/** "invoiced by AWS Marketplace", or a channel-free equivalent. */
export function invoicedBy(channel?: string | null): string {
  const name = channelName(channel);
  return name ? `invoiced by ${name}` : 'invoiced by the channel that sold it';
}

export function activityLine(event: ActivityEvent): string {
  switch (event.event) {
    case 'subscription_received':
      // An internal agreement is never "awaiting setup": nothing activates it.
      return isWaitingOnActivation(event)
        ? `New subscription received — ${event.subject} is awaiting setup.`
        : `Subscription received — ${event.subject}.`;
    case 'subscription_lifecycle':
      return `Subscription ${event.verb?.replace(/_/g, ' ')} — ${event.subject}.`;
    case 'instance_registered':
      return `Instance ${event.subject} registered — now ${event.state?.replace(/_/g, ' ')}.`;
    case 'usage_answered': {
      // The event carries its own channel; naming a different one would be a
      // guess. Without a name the sentence still reads, minus the claim.
      const by = channelName(event.channel);
      const suffix = by ? ` by ${by}` : '';
      return event.outcome === 'rejected'
        ? `Usage record rejected${suffix} — ${event.subject}.`
        : `Usage accepted${suffix} — ${event.subject}.`;
    }
    case 'metering_silence_detected':
      return `Metering gap detected on ${event.subject} — an hour went unreported.`;
    case 'metering_silence_resolved':
      return `Metering gap on ${event.subject} accounted for.`;
    default:
      return event.subject;
  }
}

/** What a data-bearing screen knows about its own request. */
export type Loaded<T> =
  | { status: 'loading' }
  | { status: 'failed'; error: string }
  | { status: 'ready'; data: T };

/** Where the portal's bearer-token endpoints live, as auth/api.ts has it. */
const API_BASE = '/portal/api';

/**
 * Fetch one portal endpoint, and report the three states honestly.
 *
 * `failed` carries the service's own refusal wording where it sent one, because
 * a screen that renders "Something went wrong" for a refused read and for an
 * unreachable host alike tells the person nothing they can act on.
 *
 * Deliberately not a cache. A page that mounts twice fetches twice; the
 * alternative is a stale figure on a billing screen, which is a worse trade
 * than a second request.
 */
export function usePortalResource<T>(path: string): Loaded<T> {
  const { authorizedFetch } = usePortalSession();
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' });

  const load = useCallback(
    async (settle: (next: Loaded<T>) => void) => {
      try {
        const response = await authorizedFetch(`${API_BASE}${path}`);
        if (!response.ok) {
          settle({ status: 'failed', error: await refusalFrom(response) });
          return;
        }
        settle({ status: 'ready', data: (await response.json()) as T });
      } catch {
        // Nothing answered, so there is no wording to report but our own.
        settle({ status: 'failed', error: 'The service could not be reached.' });
      }
    },
    [authorizedFetch, path],
  );

  useEffect(() => {
    let current = true;
    // Nothing resets the state to `loading` here. The initial value
    // already is `loading`, and a synchronous setState in an effect body
    // is a cascading render (the lint rule that says so is right). The
    // reset would only matter if `path` changed under a mounted hook,
    // which the three exported hooks below never do — each is called
    // with a literal.
    //
    // The flag is what discards a response that arrives after the screen
    // has moved on, which in React's development double-mount is every
    // first request.
    void load((next) => {
      if (current) {
        setState(next);
      }
    });
    return () => {
      current = false;
    };
  }, [load]);

  return state;
}

export const usePacks = () => usePortalResource<PacksPayload>('/packs/');
export const useBilling = () => usePortalResource<BillingPayload>('/billing/');
export const useActivity = () =>
  usePortalResource<ActivityPayload>('/activity/');

/** What the service said, or a stand-in when it said nothing readable. */
async function refusalFrom(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) {
      return body.error.charAt(0).toUpperCase() + body.error.slice(1);
    }
  } catch {
    // A refusal with no JSON body — a proxy answering instead of the
    // service. The status is all there is to report.
  }
  return `The service answered ${response.status}.`;
}
